import React, { useState, useEffect, useCallback, useRef } from 'react';
import config from '../config';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Alert,
  Checkbox,
  Chip,
  CircularProgress,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
  IconButton,
  Snackbar,
  FormHelperText,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  CameraAlt as CameraIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as ShowIcon,
  VisibilityOff as HideIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usersAPI as userAPI, companiesAPI, type UserUpdate } from '../services';
import type { Company } from '../services/companies';
import { useAuth } from '../context/AuthContext';
import { isSuperAdmin, getAllowedUserTypes } from '../utils/userPermissions';
import { getUserTypeLabel } from '../utils/userTypes';
import {
  ALL_MONITORING_FUNCTIONS,
  FUNCTION_DEPENDENCIES,
  type MonitoringFunctionId,
} from '../components/pour/PourFunctionSelector';

// Short labels for the pour-type access checkboxes (wizard titles are too long)
const POUR_TYPE_LABELS: Record<MonitoringFunctionId, string> = {
  'Temperature Only': 'Temperature Only',
  'Concrete Maturity': 'Concrete Maturity',
  'Temperature Match Curing': 'Temperature Match Curing (TMC)',
  'External Temperature and Humidity': 'External Temperature & Humidity',
  'Onsite Weather Conditions': 'Onsite Weather Conditions',
  'Calculate Concrete Evaporation Rate': 'Evaporation Rate',
};

// One-click presets covering the sensible license bundles
const POUR_TYPE_PRESETS: { label: string; functions: MonitoringFunctionId[] }[] = [
  { label: 'Temp Only', functions: ['Temperature Only'] },
  { label: 'Maturity', functions: ['Concrete Maturity'] },
  { label: 'TMC', functions: ['Temperature Match Curing'] },
  { label: 'Full suite', functions: [...ALL_MONITORING_FUNCTIONS] },
];

// Expand granted functions with their prerequisites (Maturity → Temp Only, …)
const withDependencies = (granted: MonitoringFunctionId[]): MonitoringFunctionId[] =>
  ALL_MONITORING_FUNCTIONS.filter(
    (f) => granted.includes(f) || granted.some((g) => FUNCTION_DEPENDENCIES[g].includes(f)),
  );

const MAX_AVATAR_BYTES = 5 * 1024 * 1024;
const AVATAR_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const MIN_PASSWORD_LENGTH = 8;

const EMPTY_FORM = {
  UserName: '',
  Password: '',
  EmailAddress: '',
  FirstName: '',
  LastName: '',
  Mobile: '',
  Phone: '',
  UserType: '02',
  CompanyID: '',
  CompanyName: '',
  Enable: '', // Empty = Active, "1" = Disabled
};
type FormState = typeof EMPTY_FORM;

const UserEdit: React.FC = () => {
  const { t } = useTranslation();
  const { id: userId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const superAdmin = isSuperAdmin(currentUser);
  const allowedUserTypes = getAllowedUserTypes(currentUser);
  const isNewUser = !userId || userId === 'new';
  const isSelf = !isNewUser && String(currentUser?.UserID) === String(userId);
  // Backend: avatar upload is super admin or own profile only
  const canEditAvatar = superAdmin || isSelf;
  const listPath = superAdmin ? '/users' : '/settings/users';

  const [loading, setLoading] = useState(!isNewUser);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'warning' } | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [showPassword, setShowPassword] = useState(false);
  const [touched, setTouched] = useState(false);

  const [formData, setFormData] = useState<FormState>(EMPTY_FORM);
  const originalRef = useRef<FormState>(EMPTY_FORM);
  const [dirty, setDirty] = useState(false);

  // Read-only system details (shown to super admin when present)
  const [ibutton, setIbutton] = useState<string>('');
  const [deviceUserId, setDeviceUserId] = useState<string>('');

  // Avatar
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [profileImage, setProfileImage] = useState<string>('');
  const [avatarVersion, setAvatarVersion] = useState(0);
  const [pendingAvatar, setPendingAvatar] = useState<File | null>(null);
  const [pendingPreview, setPendingPreview] = useState<string>('');
  const [uploading, setUploading] = useState(false);

  // ─── Pour type access (super admin only) ───
  // Off = inherit the company's licensed pour types (the default).
  // On  = user is narrowed to the checked functions (prerequisites auto-included).
  const [restrictPourTypes, setRestrictPourTypes] = useState(false);
  const [grantedPourTypes, setGrantedPourTypes] = useState<MonitoringFunctionId[]>([]);
  const effectivePourTypes = withDependencies(grantedPourTypes);

  const markDirty = () => setDirty(true);

  const togglePourType = (funcId: MonitoringFunctionId) => {
    markDirty();
    setGrantedPourTypes((prev) => {
      if (withDependencies(prev).includes(funcId)) {
        // Unchecking: also drop anything that depends on it
        return prev.filter(
          (f) => f !== funcId && !FUNCTION_DEPENDENCIES[f].includes(funcId),
        );
      }
      return [...prev, funcId];
    });
  };

  const fetchUser = useCallback(async () => {
    if (!userId) return;
    try {
      setLoading(true);
      setError(null);
      const user = await userAPI.getUserById(userId);
      if (!user) {
        setError('User data not found');
        return;
      }
      const loaded: FormState = {
        UserName: user.UserName || '',
        Password: '', // Never populated
        EmailAddress: user.EmailAddress || '',
        FirstName: user.FirstName || '',
        LastName: user.LastName || '',
        Mobile: user.Mobile || '',
        Phone: user.Phone || '',
        UserType: user.UserType?.trim() || '02',
        CompanyID: user.CompanyID || '',
        CompanyName: user.CompanyName || '',
        // Active (is_enabled: true) = '', Disabled = '1'
        Enable: user.is_enabled ? '' : '1',
      };
      originalRef.current = loaded;
      setFormData(loaded);
      setProfileImage(user.ProfileImage || '');
      setIbutton(user.IbuttonID || '');
      setDeviceUserId(user.DeviceUserid ? String(user.DeviceUserid) : '');

      // Pour type access: non-empty = restricted to the listed functions
      const storedFunctions = (user.AllowedMonitoringFunctions || '')
        .split(',')
        .map((s: string) => s.trim())
        .filter((s: string): s is MonitoringFunctionId =>
          (ALL_MONITORING_FUNCTIONS as string[]).includes(s),
        );
      setRestrictPourTypes(storedFunctions.length > 0);
      setGrantedPourTypes(storedFunctions);
      setDirty(false);
    } catch (err: any) {
      if (err?.response?.status === 403) {
        setError('Access denied: You can only edit users from your own company');
      } else if (err?.response?.status === 404) {
        setError('User not found');
      } else {
        setError(err?.response?.data?.detail || err?.message || 'Failed to fetch user');
      }
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (!isNewUser && userId) fetchUser();
  }, [userId, isNewUser, fetchUser]);

  // Super admin picks from all companies (create and edit)
  useEffect(() => {
    if (superAdmin) {
      companiesAPI.getCompanies().then(setCompanies).catch(() => {});
    }
  }, [superAdmin]);

  // Non-super-admin creating a user: auto-assign to their own company
  useEffect(() => {
    if (isNewUser && currentUser && !superAdmin) {
      const ownCompanyId = String(currentUser.companyid || currentUser.CompanyID || '');
      const ownCompanyName = String(
        (currentUser as any).CompanyName || (currentUser as any).companyname || '',
      );
      setFormData((prev) => ({ ...prev, CompanyID: ownCompanyId, CompanyName: ownCompanyName }));
    }
  }, [isNewUser, currentUser, superAdmin]);

  // Release the local preview URL
  useEffect(() => () => {
    if (pendingPreview) URL.revokeObjectURL(pendingPreview);
  }, [pendingPreview]);

  const handleChange = (field: keyof FormState) => (event: any) => {
    markDirty();
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleEnableChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    markDirty();
    setFormData((prev) => ({ ...prev, Enable: event.target.checked ? '' : '1' }));
  };

  const handleAvatarPick = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;

    if (!AVATAR_TYPES.includes(file.type)) {
      setError(t('userEdit.avatarBadType', 'Profile picture must be a JPG, PNG, GIF or WebP image.'));
      return;
    }
    if (file.size > MAX_AVATAR_BYTES) {
      setError(t('userEdit.avatarTooLarge', 'Profile picture must be 5 MB or smaller.'));
      return;
    }
    setError(null);

    if (isNewUser) {
      // No user id yet: hold the file and upload right after the user is created
      setPendingAvatar(file);
      setPendingPreview(URL.createObjectURL(file));
      return;
    }

    try {
      setUploading(true);
      const res = await userAPI.uploadAvatar(userId!, file);
      setProfileImage(res?.filename || profileImage);
      setAvatarVersion(Date.now()); // stable filename per user, so bust the cache
      setToast({ msg: t('userEdit.avatarUpdated', 'Profile picture updated'), severity: 'success' });
    } catch (err: any) {
      setError(err?.response?.data?.detail || t('userEdit.avatarUploadFailed', 'Failed to upload profile picture'));
    } finally {
      setUploading(false);
    }
  };

  const passwordTooShort = isNewUser && formData.Password.length > 0 && formData.Password.length < MIN_PASSWORD_LENGTH;
  const missingCompany = isNewUser && !formData.CompanyID;

  const leave = () => {
    if (dirty && !window.confirm(t('userEdit.discardChanges', 'Discard your unsaved changes?'))) return;
    navigate(listPath);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);

    if (!formData.UserName.trim()) {
      setError(t('userEdit.usernameRequired', 'Username is required'));
      return;
    }
    if (isNewUser && (!formData.Password || passwordTooShort)) {
      setError(
        t('userEdit.passwordRequired', 'Enter a password of at least {{n}} characters', { n: MIN_PASSWORD_LENGTH }),
      );
      return;
    }
    if (missingCompany) {
      setError(t('userEdit.companyRequired', 'Choose a company for this user'));
      return;
    }

    try {
      setSaving(true);
      setError(null);

      if (isNewUser) {
        const created = await userAPI.createUser({
          UserName: formData.UserName.trim(),
          Password: formData.Password,
          CompanyID: formData.CompanyID,
          EmailAddress: formData.EmailAddress || undefined,
          FirstName: formData.FirstName || undefined,
          LastName: formData.LastName || undefined,
          Mobile: formData.Mobile || undefined,
          Phone: formData.Phone || undefined,
          UserType: formData.UserType,
          Enable: formData.Enable || undefined,
          AllowedMonitoringFunctions:
            superAdmin && restrictPourTypes && effectivePourTypes.length > 0
              ? effectivePourTypes.join(', ')
              : undefined,
        });

        let avatarFailed = false;
        if (pendingAvatar && created?.UserID != null) {
          try {
            await userAPI.uploadAvatar(String(created.UserID), pendingAvatar);
          } catch {
            avatarFailed = true;
          }
        }
        setDirty(false);
        setToast(
          avatarFailed
            ? { msg: t('userEdit.createdNoAvatar', 'User created, but the profile picture could not be uploaded. Add it from the edit page.'), severity: 'warning' }
            : { msg: t('userEdit.created', 'User created'), severity: 'success' },
        );
        setTimeout(() => navigate(listPath), avatarFailed ? 2500 : 1000);
      } else {
        // Send only what changed: the backend rejects company/type/status
        // fields for roles that may not touch them, even when unchanged.
        const orig = originalRef.current;
        const updateData: UserUpdate = {};
        const text = ['UserName', 'EmailAddress', 'FirstName', 'LastName', 'Mobile', 'Phone'] as const;
        text.forEach((f) => {
          if (formData[f] !== orig[f]) (updateData as any)[f] = formData[f]; // '' clears the field
        });
        if (formData.UserType !== orig.UserType) updateData.UserType = formData.UserType;
        if (formData.CompanyID !== orig.CompanyID) updateData.CompanyID = formData.CompanyID;
        if (formData.Enable !== orig.Enable) updateData.Enable = formData.Enable || undefined;
        if (superAdmin) {
          // "" clears the restriction (user inherits company access again)
          updateData.AllowedMonitoringFunctions =
            restrictPourTypes && effectivePourTypes.length > 0 ? effectivePourTypes.join(', ') : '';
        }

        await userAPI.updateUser(userId!, updateData);
        setDirty(false);
        setToast({ msg: t('userEdit.saved', 'Changes saved'), severity: 'success' });
        setTimeout(() => navigate(listPath), 1000);
      }
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : t('userEdit.saveFailed', 'Failed to save user'));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  const displayName =
    `${formData.FirstName} ${formData.LastName}`.trim() || formData.UserName || t('userEdit.newUser', 'New user');
  const avatarSrc =
    pendingPreview ||
    (profileImage
      ? `${config.apiBaseUrl}/files/user-avatars/${profileImage}${avatarVersion ? `?v=${avatarVersion}` : ''}`
      : undefined);
  const showAvatarUpload = canEditAvatar && !(isNewUser && !superAdmin);
  const ownCompanyLabel = formData.CompanyName || t('common.noCompany');
  const companyEditable = superAdmin;

  return (
    <Box sx={{ p: 3, pb: 12, maxWidth: 960, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Button startIcon={<ArrowBackIcon />} onClick={leave} variant="outlined">
          {t('common.back')}
        </Button>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {isNewUser
            ? t('userEdit.createTitle', 'Create new user')
            : t('userEdit.editTitle', 'Edit user')}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit} noValidate>
        {/* Identity */}
        <Paper sx={{ p: 3, mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'center', sm: 'flex-start' } }}>
            <Box sx={{ textAlign: 'center' }}>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleAvatarPick}
                accept={AVATAR_TYPES.join(',')}
                style={{ display: 'none' }}
                data-testid="avatar-input"
              />
              <Box
                onClick={() => showAvatarUpload && fileInputRef.current?.click()}
                sx={{
                  position: 'relative',
                  display: 'inline-block',
                  cursor: showAvatarUpload ? 'pointer' : 'default',
                  '&:hover .avatar-overlay': { opacity: showAvatarUpload ? 1 : 0 },
                }}
              >
                <Avatar
                  src={avatarSrc}
                  alt={`${displayName} avatar`}
                  sx={{ width: 120, height: 120, fontSize: '3rem', bgcolor: 'secondary.main' }}
                >
                  {(formData.FirstName || formData.UserName || '?')[0]?.toUpperCase()}
                </Avatar>
                {showAvatarUpload && (
                  <Box
                    className="avatar-overlay"
                    sx={{
                      position: 'absolute', inset: 0, borderRadius: '50%',
                      bgcolor: 'rgba(0,0,0,0.5)', display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                      opacity: uploading ? 1 : 0, transition: 'opacity 0.2s',
                    }}
                  >
                    {uploading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : <CameraIcon sx={{ color: '#fff', fontSize: 32 }} />}
                  </Box>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1, maxWidth: 140 }}>
                {showAvatarUpload
                  ? t('userEdit.avatarHint', 'Click to change. JPG, PNG, GIF or WebP, up to 5 MB.')
                  : isNewUser
                    ? t('userEdit.avatarAfterCreate', 'A super admin or the user can add a picture after the account is created.')
                    : t('userEdit.avatarNoRights', 'Only a super admin or the user can change this picture.')}
              </Typography>
            </Box>

            <Box sx={{ flex: 1, width: '100%', display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <TextField
                label={t('userEdit.username')}
                value={formData.UserName}
                onChange={handleChange('UserName')}
                required
                fullWidth
                autoComplete="off"
                error={touched && !formData.UserName.trim()}
                helperText={t('userEdit.usernameHint', 'Used to sign in')}
              />
              {isNewUser ? (
                <TextField
                  label={t('userEdit.password')}
                  type={showPassword ? 'text' : 'password'}
                  value={formData.Password}
                  onChange={handleChange('Password')}
                  required
                  fullWidth
                  autoComplete="new-password"
                  error={passwordTooShort || (touched && !formData.Password)}
                  helperText={t('userEdit.passwordHint', 'At least {{n}} characters', { n: MIN_PASSWORD_LENGTH })}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setShowPassword((v) => !v)}
                          aria-label={showPassword ? t('userEdit.hidePassword', 'Hide password') : t('userEdit.showPassword', 'Show password')}
                        >
                          {showPassword ? <HideIcon fontSize="small" /> : <ShowIcon fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Button
                    startIcon={<LockIcon />}
                    variant="outlined"
                    onClick={() => navigate(`/users/${userId}/change-password`)}
                  >
                    {t('userEdit.changePassword', 'Change password')}
                  </Button>
                </Box>
              )}
              <TextField label={t('userEdit.firstName')} value={formData.FirstName} onChange={handleChange('FirstName')} fullWidth />
              <TextField label={t('userEdit.lastName')} value={formData.LastName} onChange={handleChange('LastName')} fullWidth />
              <TextField label={t('userEdit.emailAddress')} type="email" value={formData.EmailAddress} onChange={handleChange('EmailAddress')} fullWidth />
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <TextField label={t('userEdit.mobile')} value={formData.Mobile} onChange={handleChange('Mobile')} fullWidth />
                <TextField label={t('userEdit.phone')} value={formData.Phone} onChange={handleChange('Phone')} fullWidth />
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* Access */}
        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            {t('userEdit.access', 'Company and access')}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 2 }}>
            {companyEditable ? (
              <FormControl fullWidth required error={touched && missingCompany}>
                <InputLabel id="user-company-label">{t('userEdit.company')}</InputLabel>
                <Select
                  labelId="user-company-label"
                  value={companies.some((c) => c.companyid === formData.CompanyID) ? formData.CompanyID : ''}
                  onChange={handleChange('CompanyID')}
                  label={t('userEdit.company')}
                >
                  {companies.map((c) => (
                    <MenuItem key={c.companyid} value={c.companyid}>
                      {c.companyname}
                    </MenuItem>
                  ))}
                </Select>
                {!isNewUser && formData.CompanyName && (
                  <FormHelperText>
                    {t('userEdit.currentCompany', 'Currently: {{name}}', { name: formData.CompanyName })}
                  </FormHelperText>
                )}
              </FormControl>
            ) : (
              <TextField
                label={t('userEdit.company')}
                value={ownCompanyLabel}
                fullWidth
                disabled
                helperText={t('userEdit.companyLocked', 'Users are always added to your company')}
              />
            )}

            <FormControl fullWidth required>
              <InputLabel id="user-type-label">{t('userEdit.userType')}</InputLabel>
              <Select
                labelId="user-type-label"
                value={formData.UserType || ''}
                onChange={handleChange('UserType')}
                label={t('userEdit.userType')}
                disabled={isSelf}
              >
                {Array.from(new Set([...allowedUserTypes, formData.UserType].filter(Boolean))).map((type) => (
                  <MenuItem key={type} value={type} disabled={!allowedUserTypes.includes(type)}>
                    {getUserTypeLabel(type)}
                  </MenuItem>
                ))}
              </Select>
              {isSelf && (
                <FormHelperText>{t('userEdit.ownTypeLocked', 'You cannot change your own role')}</FormHelperText>
              )}
            </FormControl>

            {['04', '07'].includes(formData.UserType || '') && (
              <Alert severity="info" sx={{ gridColumn: { md: '1 / -1' } }}>
                {t(
                  'userEdit.projectScopedWarning',
                  'This role only sees projects and pours it is assigned to. After saving, add this user to at least one project (Projects > project > Users tab), or they will see an empty dashboard.',
                )}
              </Alert>
            )}

            {!isSelf && (
              <FormControlLabel
                control={<Switch checked={formData.Enable !== '1'} onChange={handleEnableChange} />}
                label={t('userEdit.userEnabled')}
              />
            )}
          </Box>

          {superAdmin && !isNewUser && (ibutton || deviceUserId) && (
            <Box sx={{ display: 'flex', gap: 4, mt: 2, flexWrap: 'wrap' }}>
              {ibutton && (
                <Typography variant="body2" color="text.secondary">
                  {t('userEdit.iButtonId', 'iButton ID')}: {ibutton}
                </Typography>
              )}
              {deviceUserId && (
                <Typography variant="body2" color="text.secondary">
                  {t('userEdit.deviceUserId', 'Device user ID')}: {deviceUserId}
                </Typography>
              )}
            </Box>
          )}
        </Paper>

        {/* Advanced access: pour types (super admin only) */}
        {superAdmin && (
          <Accordion disableGutters sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6" sx={{ fontSize: '1.05rem' }}>
                {t('userEdit.advancedAccess', 'Advanced access')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 2, alignSelf: 'center' }}>
                {restrictPourTypes
                  ? t('userEdit.pourTypesRestrictedSummary', 'Pour types restricted')
                  : t('userEdit.pourTypesInheritSummary', 'Pour types inherited from company')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <FormControlLabel
                control={
                  <Switch
                    checked={restrictPourTypes}
                    onChange={(e) => {
                      markDirty();
                      setRestrictPourTypes(e.target.checked);
                    }}
                  />
                }
                label={t('userEdit.restrictPourTypes', 'Restrict pour types for this user')}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {restrictPourTypes
                  ? t(
                      'userEdit.pourTypeAccessRestrictedHint',
                      'The user can only create pours of the selected types. Prerequisites are included automatically (e.g. Concrete Maturity requires Temperature Only). Access is always capped by the company’s licensed features.',
                    )
                  : t(
                      'userEdit.pourTypeAccessInheritHint',
                      'The user inherits the pour types licensed to their company (default).',
                    )}
              </Typography>

              {restrictPourTypes && (
                <Box>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                    {POUR_TYPE_PRESETS.map((preset) => (
                      <Chip
                        key={preset.label}
                        label={preset.label}
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          markDirty();
                          setGrantedPourTypes(preset.functions);
                        }}
                      />
                    ))}
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                    {ALL_MONITORING_FUNCTIONS.map((funcId) => {
                      const checked = effectivePourTypes.includes(funcId);
                      const autoIncluded = checked && !grantedPourTypes.includes(funcId);
                      return (
                        <FormControlLabel
                          key={funcId}
                          control={<Checkbox checked={checked} onChange={() => togglePourType(funcId)} />}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {POUR_TYPE_LABELS[funcId]}
                              {autoIncluded && (
                                <Chip
                                  label={t('pourFunction.required', 'Required')}
                                  size="small"
                                  sx={{ height: 18, fontSize: '0.7rem' }}
                                />
                              )}
                            </Box>
                          }
                        />
                      );
                    })}
                  </Box>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        )}

        {/* Sticky action bar */}
        <Paper
          elevation={8}
          sx={{
            position: 'sticky', bottom: 0, zIndex: 10, mt: 3, p: 1.5,
            display: 'flex', gap: 2, justifyContent: 'flex-end', alignItems: 'center',
          }}
        >
          {dirty && (
            <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto', pl: 1 }}>
              {t('userEdit.unsaved', 'Unsaved changes')}
            </Typography>
          )}
          <Button variant="outlined" onClick={leave} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="contained" startIcon={<SaveIcon />} disabled={saving}>
            {saving ? (
              <CircularProgress size={20} />
            ) : isNewUser ? (
              t('userEdit.createButton')
            ) : (
              t('userEdit.saveChanges', 'Save changes')
            )}
          </Button>
        </Paper>
      </form>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} variant="filled">
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default UserEdit;
